import React, { forwardRef } from 'react';
import cn from 'classnames';
import { SIZES, LABEL_TYPES } from '../classnames';
import CLASSNAMES from './classnames';
import parseClassnames from '../../helpers/parseClassnames';
import transition from '../../styles/transition';
import T from '../../Text';
import { getDefaultIconValues, IconItemType } from '../helpers';
import { InverseButtonProps } from './types';

const InverseButton = forwardRef<any, InverseButtonProps>(
  (
    {
      children,
      label,
      size = 'm',
      as,
      white,
      gray,
      darkMode,
      disabled,
      full,
      leftIcon: leftIconProp,
      rightIcon: rightIconProp,
      href,
      className = '',
      style,
      ...other
    },
    ref,
  ) => {
    const leftIcon: IconItemType = getDefaultIconValues(leftIconProp);
    const rightIcon: IconItemType = getDefaultIconValues(rightIconProp);
    let Tag: keyof JSX.IntrinsicElements = 'button';

    if (as) {
      Tag = as;
    } else if (href) {
      Tag = 'a';
    }

    let buttonTypeClassNames = disabled
      ? CLASSNAMES['default-disabled']
      : CLASSNAMES.default;

    if (darkMode) {
      buttonTypeClassNames = disabled
        ? CLASSNAMES['darkMode-disabled']
        : CLASSNAMES.darkMode;
    }

    if (gray) {
      buttonTypeClassNames = darkMode
        ? CLASSNAMES['gray-dark']
        : CLASSNAMES.gray;

      if (disabled) {
        buttonTypeClassNames = darkMode
          ? CLASSNAMES['darkMode-disabled']
          : CLASSNAMES['gray-disabled'];
      }
    }

    if (white) {
      buttonTypeClassNames = darkMode
        ? CLASSNAMES['white-dark']
        : CLASSNAMES.white;

      if (disabled) {
        buttonTypeClassNames = CLASSNAMES['white-disabled'];
      }
    }

    return (
      <Tag
        className={parseClassnames(
          className,
          cn(
            {
              'w-full': full,
            },
            SIZES[size],
            buttonTypeClassNames,
          ),
        )}
        style={{
          transition: transition(['background-color', 'color', 'opacity']),
          ...style,
        }}
        disabled={disabled}
        href={href}
        ref={ref}
        {...other}
      >
        {leftIcon.component && (
          <leftIcon.component
            size={leftIcon.size}
            className={cn(
              {
                'fill-current': leftIcon.hasFill,
                'stroke-current': leftIcon.hasStroke,
              },
              'flex-shrink-0',
            )}
            ignoreColor
          />
        )}
        {label ? (
          <T
            type={LABEL_TYPES[size]}
            color="inherit"
            className={cn({
              'ml-8': !!leftIcon.component,
              'mr-8': !!rightIcon.component,
            })}
          >
            {label}
          </T>
        ) : (
          children
        )}
        {rightIcon.component && (
          <rightIcon.component
            size={rightIcon.size}
            className={cn(
              {
                'fill-current': rightIcon.hasFill,
                'stroke-current': rightIcon.hasStroke,
              },
              'flex-shrink-0',
            )}
            ignoreColor
          />
        )}
      </Tag>
    );
  },
);

export default InverseButton;
